import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import type { GrowthWheelVersionMeta } from '../../types/GrowthWheel'
import { toWheelVersionDisplayStatus } from '../../types/GrowthWheel'
import {
  useActivateGrowthWheelVersionMutation,
  useCreateGrowthWheelVersionMutation,
  useDeleteGrowthWheelVersionMutation,
} from '../../redux/growth-wheel.endpoints'
import { VersionDropdown } from './VersionDropdown'
import { CreateVersionAction } from './CreateVersionAction'
import { SetActiveVersionAction } from './SetActiveVersionAction'
import { DeleteVersionAction } from './DeleteVersionAction'
import { WheelVersionStatusBanner, getErrorMessage } from './wheelVersionBarUtils'

interface WheelVersionBarProps {
  roleName?: string
  versions: GrowthWheelVersionMeta[]
  selectedVersionId: number
}

export function WheelVersionBar({
  roleName,
  versions,
  selectedVersionId,
}: WheelVersionBarProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const [createVersion, { isLoading: isCreatingDraft }] =
    useCreateGrowthWheelVersionMutation()
  const [activateVersion, { isLoading: isActivating }] =
    useActivateGrowthWheelVersionMutation()
  const [deleteVersion, { isLoading: isDeleting }] =
    useDeleteGrowthWheelVersionMutation()

  const hasDraft = versions.some(v => v.status === 'draft')

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const sorted = [...versions].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt),
  )
  const current = versions.find(v => v.id === selectedVersionId)
  if (!current) return null

  const currentDisplayStatus = toWheelVersionDisplayStatus(current.status)

  const handleSelectVersion = (versionId: number) => {
    if (versionId === selectedVersionId) return
    navigate(`/edit-growth-wheel/${versionId}`)
  }

  const handleCreateDraft = async () => {
    const activeVersion = versions.find(v => v.status === 'active')
    if (!activeVersion) {
      toast.error('No active version found. Cannot create a draft.', {
        toastId: 'create-gw-version-no-active',
      })
      return
    }

    try {
      const draft = await createVersion({ growthWheelId: activeVersion.id }).unwrap()
      toast.success(`Draft ${draft.roleName} ${draft.label} created.`)
      navigate(`/edit-growth-wheel/${draft.id}`)
    } catch (err: unknown) {
      const message = getErrorMessage(err, 'Could not create draft version.')
      toast.error(message, { toastId: 'create-gw-version-error' })
    }
  }

  const handleSetActive = async () => {
    try {
      const activated = await activateVersion({
        growthWheelId: selectedVersionId,
      }).unwrap()
      toast.success(`${current.roleName} ${activated.label} is now active.`)
    } catch (err: unknown) {
      const message =getErrorMessage(err, 'Could not activate this version.')
      toast.error(message, { toastId: 'activate-gw-version-error' })
    }
  }

  const handleDelete = async () => {
    try {
      await deleteVersion({ growthWheelId: selectedVersionId }).unwrap()
    } catch (err: unknown) {
      const message = getErrorMessage(err, 'Could not delete this version.')
      toast.error(message, { toastId: 'delete-gw-version-error' })
      return
    }

    const remaining = versions.filter(v => v.id !== selectedVersionId)
    const activeVersion = remaining.find(v => v.status === 'active')
    const fallback = activeVersion ?? remaining[0]

    toast.success(`${current.roleName} ${current.label} deleted.`)

    if (fallback) {
      navigate(`/edit-growth-wheel/${fallback.id}`)
    } else {
      navigate('/edit-growth-wheel')
    }
  }

  return (
    <>
      <div className="gw-version-bar__card" ref={ref}>
        <div className="gw-version-bar">
          <div className="gw-version-bar__meta">
            {roleName ? (
              <>
                <div>
                  <p className="gw-version-bar__label">Role</p>
                  <p className="gw-version-bar__role-name">{roleName}</p>
                </div>
                <div className="gw-version-bar__divider" aria-hidden />
              </>
            ) : null}
            <VersionDropdown
              current={current}
              currentDisplayStatus={currentDisplayStatus}
              sorted={sorted}
              selectedVersionId={selectedVersionId}
              open={open}
              setOpen={setOpen}
              onSelectVersion={handleSelectVersion}
            />
          </div>
          <div className="gw-version-bar__actions">
            {current.status !== 'active' ? (
              <SetActiveVersionAction
                roleName={roleName}
                isHistorical={current.status === 'archived'}
                isActivating={isActivating}
                onSetActive={handleSetActive}
              />
            ) : null}
            <CreateVersionAction
              hasDraft={hasDraft}
              isCreatingDraft={isCreatingDraft}
              onCreateDraft={handleCreateDraft}
            />
            {current.status !== 'active' ? (
              <DeleteVersionAction
                versionLabel={current.label}
                versionStatus={current.status}
                canDelete={current.canDelete}
                hasEvaluations={current.hasEvaluations}
                isDeleting={isDeleting}
                onDelete={handleDelete}
              />
            ) : null}
          </div>
        </div>
      </div>

      <WheelVersionStatusBanner status={currentDisplayStatus} />
    </>
  )
}
